import { CurrencyCell } from '@components/DataTable/CurrencyCell';
import { DateDisplay } from '@components/DateDisplay';
import { EllipsisText } from '@components/EllipsisText';
import { Rut } from '@components/Rut';
import { type SaleDetail } from '@invoices/api/sales/find/types';
import { INVOICES_TABLE } from '@locales/invoices';
import { type ColumnDef } from '@tanstack/react-table';
import { getSellDocumentTypeDescription } from '@utils/documents/getSellDocumentTypeDescription';
import { formatRut } from '@utils/rut/formatRut';

const DATE_COLUMN_SIZE = 140;
const NAME_COLUMN_SIZE = 190;
const RUT_COLUMN_SIZE = 125;
const EMPTY_VALUE = '-';

const columns: ColumnDef<SaleDetail>[] = [
  {
    id: 'clientCompanyName',
    accessorKey: 'clientCompanyName',
    header: INVOICES_TABLE.HEADERS.CLIENT_COMPANY_NAME,
    cell: ({ row }) => {
      const { clientCompanyName } = row.original;

      return (
        <EllipsisText className="text-start">{clientCompanyName}</EllipsisText>
      );
    },
    size: NAME_COLUMN_SIZE,
  },
  {
    id: 'clientRut',
    accessorKey: 'clientRut',
    cell: ({ row }) => {
      const { clientRut } = row.original;

      return <Rut fullRut={clientRut} />;
    },
    header: INVOICES_TABLE.HEADERS.CLIENT_RUT,
    size: RUT_COLUMN_SIZE,
  },
  {
    id: 'debtorCompanyName',
    accessorKey: 'debtorCompanyName',
    header: INVOICES_TABLE.HEADERS.DEBTOR_COMPANY_NAME,
    cell: ({ row }) => {
      const { debtorCompanyName } = row.original;

      return (
        <EllipsisText className="text-start">{debtorCompanyName}</EllipsisText>
      );
    },
    size: NAME_COLUMN_SIZE,
  },
  {
    id: 'debtorRut',
    accessorKey: 'debtorRut',
    cell: ({ row }) => {
      const { debtorRut } = row.original;

      return <p className="text-nowrap">{formatRut(debtorRut)}</p>;
    },
    header: INVOICES_TABLE.HEADERS.DEBTOR_RUT,
    size: RUT_COLUMN_SIZE,
  },
  {
    id: 'debtorCompanyRanking',
    accessorKey: 'debtorCompanyRanking',
    header: INVOICES_TABLE.HEADERS.DEBTOR_COMPANY_RANKING,
    enableSorting: false,
    size: 80,
    meta: {
      className: 'text-nowrap tabular-nums text-end',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'receptionDate',
    accessorKey: 'receptionDate',
    header: INVOICES_TABLE.HEADERS.RECEPTION_DATE,
    cell: ({ row }) => {
      const { receptionDate } = row.original;

      return <DateDisplay date={receptionDate} />;
    },
    meta: {
      className: 'tabular-nums truncate',
    },
    size: DATE_COLUMN_SIZE,
  },
  {
    id: 'totalAmount',
    header: INVOICES_TABLE.HEADERS.TOTAL_AMOUNT,
    accessorKey: 'totalAmount',
    accessorFn: row => row.amount - row.creditNoteAmount,
    enableSorting: false,
    cell: CurrencyCell,
    meta: {
      className: 'text-nowrap tabular-nums text-end',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'folio',
    accessorKey: 'folio',
    header: INVOICES_TABLE.HEADERS.FOLIO,
    meta: {
      className: 'text-nowrap tabular-nums text-end',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'documentType',
    accessorKey: 'documentType',
    header: INVOICES_TABLE.HEADERS.DOCUMENT_TYPE,
    cell: ({ row }) => {
      const { documentType } = row.original;
      const description = getSellDocumentTypeDescription(documentType);

      return (
        <EllipsisText className="text-start">
          ({documentType}) {description}
        </EllipsisText>
      );
    },
  },
  {
    id: 'acknowledgmentDate',
    accessorKey: 'acknowledgmentDate',
    header: INVOICES_TABLE.HEADERS.ACKNOWLEDGMENT_DATE,
    cell: ({ row }) => {
      const { acknowledgmentDate } = row.original;

      return <DateDisplay date={acknowledgmentDate} />;
    },
    meta: {
      className: 'truncate',
    },
    size: DATE_COLUMN_SIZE,
  },
  {
    id: 'claimDate',
    accessorKey: 'claimDate',
    header: INVOICES_TABLE.HEADERS.CLAIM_DATE,
    cell: ({ row }) => {
      const { claimDate } = row.original;

      return <DateDisplay date={claimDate} />;
    },
    meta: {
      className: 'truncate',
    },
    size: DATE_COLUMN_SIZE,
  },
  {
    id: 'assigneeCompanyName',
    accessorKey: 'assigneeCompanyName',
    header: INVOICES_TABLE.HEADERS.ASSIGNEE_COMPANY_NAME,
    cell: ({ row }) => {
      const { assigneeCompanyName } = row.original;
      if (assigneeCompanyName.length === 0) return EMPTY_VALUE;

      return (
        <EllipsisText className="text-start">
          {assigneeCompanyName}
        </EllipsisText>
      );
    },
    size: NAME_COLUMN_SIZE,
  },
  {
    id: 'assigneeRut',
    accessorKey: 'assigneeRut',
    header: INVOICES_TABLE.HEADERS.ASSIGNEE_RUT,
    cell: ({ row }) => {
      const { assigneeRut } = row.original;
      if (assigneeRut === null) {
        return EMPTY_VALUE;
      }

      return <Rut fullRut={assigneeRut} />;
    },
    size: RUT_COLUMN_SIZE,
  },
  {
    id: 'assignmentDate',
    accessorKey: 'assignmentDate',
    header: INVOICES_TABLE.HEADERS.ASSIGNMENT_DATE,
    cell: ({ row }) => {
      const { assignmentDate } = row.original;

      return <DateDisplay date={assignmentDate} />;
    },
    meta: {
      className: 'truncate',
    },
    size: DATE_COLUMN_SIZE,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: INVOICES_TABLE.HEADERS.STATUS,
    enableSorting: false,
    cell: ({ row }) => {
      const { status } = row.original;

      return <EllipsisText className="text-start">{status}</EllipsisText>;
    },
  },
  {
    id: 'amount',
    accessorKey: 'amount',
    header: INVOICES_TABLE.HEADERS.AMOUNT,
    cell: CurrencyCell,
    meta: {
      className: 'text-nowrap tabular-nums text-end',
      headClassName: '*:float-end',
    },
  },
  {
    id: 'creditNoteAmount',
    accessorKey: 'creditNoteAmount',
    header: INVOICES_TABLE.HEADERS.CREDIT_NOTE_AMOUNT,
    cell: CurrencyCell,
    meta: {
      className: 'text-nowrap tabular-nums text-end',
      headClassName: '*:float-end',
    },
  },
];

export { columns };
